import React from 'react'
import {
  Mail,
  Plus,
  Search,
  Filter,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Clock,
  CheckCircle,
  XCircle,
  Paperclip,

  ArrowLeft,
  ArrowRight,
  User,
  Calendar,
  FileText,
  Upload,
  X,
  Edit3,
  Layers,
  Files,
} from 'lucide-react'

// ─── Section Wrapper ─────────────────────────────────────────────────────────
function Section({ number, title, children }) {
  return (
    <section className="space-y-4" data-section-number={number}>
      <div className="flex items-center gap-2">
        <span className="w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center">{number}</span>
        <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
      </div>
      {children}
    </section>
  )
}

// ─── Section 1: Page Header Mockup ───────────────────────────────────────────
function PageHeaderMockup() {
  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Email Queue</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Monitor and manage incoming email submissions</p>
        </div>
        <div className="flex gap-3">
          <button className="inline-flex items-center gap-2 px-4 py-2.5 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-lg border border-gray-200 dark:border-gray-700 cursor-default">
            <RefreshCw className="h-4 w-4" />
            Refresh
          </button>
          <button className="inline-flex items-center gap-2 px-4 py-2.5 bg-navy-800 text-white text-sm font-medium rounded-lg cursor-default">
            <Plus className="h-4 w-4" />
            Ingest Email
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Section 2: Stats Cards Mockup ───────────────────────────────────────────
function StatsCardsMockup() {
  const stats = [
    { value: '142', label: 'Total Emails', style: 'bg-navy-800 text-white', valueColor: 'text-white', labelColor: 'text-white/70' },
    { value: '8', label: 'Queued', style: 'bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800', valueColor: 'text-gray-900 dark:text-gray-100', labelColor: 'text-gray-500 dark:text-gray-400' },
    { value: '3', label: 'Processing', style: 'bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800', valueColor: 'text-gray-900 dark:text-gray-100', labelColor: 'text-gray-500 dark:text-gray-400' },
    { value: '126', label: 'Processed', style: 'bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800', valueColor: 'text-gray-900 dark:text-gray-100', labelColor: 'text-gray-500 dark:text-gray-400' },
  ]

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {stats.map((s) => (
        <div key={s.label} className={`rounded-xl p-4 ${s.style}`}>
          <p className={`text-2xl font-bold ${s.valueColor}`}>{s.value}</p>
          <p className={`text-sm mt-1 ${s.labelColor}`}>{s.label}</p>
        </div>
      ))}
    </div>
  )
}

// ─── Section 3: Status Tabs Mockup ───────────────────────────────────────────
function StatusTabsMockup() {
  const tabs = [
    { label: 'All', icon: Mail, active: true },
    { label: 'Queued', icon: Clock, active: false },
    { label: 'Processing', icon: RefreshCw, active: false },
    { label: 'Processed', icon: CheckCircle, active: false },
    { label: 'Failed', icon: XCircle, active: false },
  ]

  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-5">
      <div className="flex items-center gap-2 overflow-x-auto pb-2">
        {tabs.map((tab) => (
          <button
            key={tab.label}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap cursor-default ${
              tab.active
                ? 'bg-navy-800 text-white'
                : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-700'
            }`}
          >
            <tab.icon className="h-4 w-4" />
            {tab.label}
          </button>
        ))}
      </div>
      <p className="text-xs text-gray-500 dark:text-gray-400 mt-3">
        Click a tab to filter the email list by status. The active tab is highlighted in navy.
      </p>
    </div>
  )
}

// ─── Section 4: Search & Time Filter Mockup ──────────────────────────────────
function SearchFilterMockup() {
  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-5">
      <div className="flex flex-col md:flex-row md:items-center gap-4">
        {/* Search */}
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
          <input
            type="text"
            placeholder="Search by subject, sender..."
            readOnly
            className="w-full pl-10 pr-4 py-2.5 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300"
          />
        </div>

        {/* Time Range */}
        <div className="flex items-center gap-2">
          <Filter className="h-5 w-5 text-gray-400" />
          <select
            disabled
            className="px-3 py-2.5 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300"
            defaultValue=""
          >
            <option value="">All Time</option>
            <option value="today">Today</option>
            <option value="week">This Week</option>
            <option value="month">This Month</option>
          </select>
        </div>
      </div>

      {/* Annotations */}
      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3 text-xs text-gray-500 dark:text-gray-400">
        <div className="flex items-start gap-2 bg-gray-50 dark:bg-gray-800 rounded-lg p-2.5">
          <Search className="h-3.5 w-3.5 mt-0.5 text-navy-600 dark:text-navy-400 flex-shrink-0" />
          <span><strong className="text-gray-700 dark:text-gray-300">Search</strong> - searches by email subject or sender address; press Enter to apply</span>
        </div>
        <div className="flex items-start gap-2 bg-gray-50 dark:bg-gray-800 rounded-lg p-2.5">
          <Clock className="h-3.5 w-3.5 mt-0.5 text-navy-600 dark:text-navy-400 flex-shrink-0" />
          <span><strong className="text-gray-700 dark:text-gray-300">Time Range</strong> - filter to All Time, Today, This Week, or This Month</span>
        </div>
      </div>
    </div>
  )
}

// ─── Section 5: Email Table Mockup ───────────────────────────────────────────
function EmailTableMockup() {
  const emails = [
    { subject: 'PO-2024-0813 Purchase Order', sender: 'procurement@techsupply.co', docs: 2, status: 'processed', received: 'May 19, 2025', hasRequest: true },
    { subject: 'Invoice INV-089 for Q2', sender: 'billing@acme.com', docs: 1, status: 'processed', received: 'May 18, 2025', hasRequest: true },
    { subject: 'Contract Renewal Notice', sender: 'legal@partnerco.io', docs: 3, status: 'processing', received: 'May 20, 2025', hasRequest: false },
    { subject: 'Delivery Confirmation #4521', sender: 'logistics@shipper.com', docs: 1, status: 'pending', received: 'May 20, 2025', hasRequest: false },
    { subject: 'Failed: Corrupted attachment', sender: 'unknown@spam.net', docs: 0, status: 'failed', received: 'May 19, 2025', hasRequest: false },
  ]

  const statusStyles = {
    pending: 'bg-yellow-50 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-300',
    processing: 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-300',
    processed: 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-300',
    failed: 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-300',
  }

  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Subject</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Sender</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Documents</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Received</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50 dark:divide-gray-800">
            {emails.map((email, i) => (
              <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                <td className="px-4 py-3">
                  <span className="font-medium text-gray-900 dark:text-gray-100 text-sm">{email.subject}</span>
                  {email.hasRequest && (
                    <span className="block text-xs text-gray-400 dark:text-gray-500 mt-0.5">View Request →</span>
                  )}
                </td>
                <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">{email.sender}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1.5 text-gray-500">
                    <Paperclip className="h-4 w-4" />
                    <span className="text-sm">{email.docs}</span>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${statusStyles[email.status]}`}>
                    {email.status}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">{email.received}</td>
                <td className="px-4 py-3">
                  <button className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 cursor-default">
                    <Edit3 className="h-3.5 w-3.5" />
                    View
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="px-5 py-4 border-t border-gray-100 dark:border-gray-800 flex items-center justify-between">
        <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
          <span>Showing 1–5 of 142 results</span>
          <span className="border-l border-gray-200 dark:border-gray-700 h-4" />
          <span className="flex items-center gap-1.5">
            Rows per page
            <select disabled className="px-2 py-1 border border-gray-200 dark:border-gray-700 rounded text-sm dark:bg-gray-800 dark:text-gray-200">
              <option>10</option>
              <option>25</option>
              <option>50</option>
            </select>
          </span>
        </div>
        <div className="flex items-center gap-1">
          <button className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-400 rounded cursor-default opacity-50">
            <ChevronLeft className="h-4 w-4" />
            Previous
          </button>
          <button className="w-8 h-8 rounded text-sm font-medium bg-navy-800 text-white">1</button>
          <button className="w-8 h-8 rounded text-sm font-medium text-gray-600 dark:text-gray-400">2</button>
          <span className="px-2 text-gray-400 dark:text-gray-500">...</span>
          <button className="w-8 h-8 rounded text-sm font-medium text-gray-600 dark:text-gray-400">15</button>
          <button className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-500 dark:text-gray-400 rounded cursor-default">
            Next
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Section 6: Ingest Email Modal Mockup ────────────────────────────────────
function IngestEmailMockup() {
  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden max-w-2xl">
      {/* Modal Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center">
          <Mail className="h-5 w-5 text-navy-600 dark:text-navy-400 mr-2" />
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Ingest Email</h2>
        </div>
        <button className="text-gray-400 cursor-default">
          <X className="h-6 w-6" />
        </button>
      </div>

      {/* Modal Body */}
      <div className="px-6 py-4 space-y-4">
        {/* Subject */}
        <div>
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">Subject <span className="text-red-500">*</span></label>
          <input
            type="text"
            readOnly
            value="PO-2024-0813 Purchase Order"
            className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300"
          />
        </div>

        {/* Sender */}
        <div>
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">Sender <span className="text-red-500">*</span></label>
          <input
            type="text"
            readOnly
            value="procurement@techsupply.co"
            className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300"
          />
        </div>

        {/* Recipients */}
        <div>
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">Recipients</label>
          <input
            type="text"
            readOnly
            placeholder="recipient1@example.com, recipient2@example.com"
            className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-400 dark:text-gray-500"
          />
          <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-1">Comma-separated email addresses</p>
        </div>

        {/* Body */}
        <div>
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">Body</label>
          <div className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-400 dark:text-gray-500 h-16">
            Email body content...
          </div>
        </div>

        {/* Template + Received At */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">Template <span className="text-red-500">*</span></label>
            <select disabled className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300">
              <option>Purchase Order Analyzer</option>
            </select>
            <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-1">Only templates with analyzers appear here</p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">Received At</label>
            <input
              type="text"
              readOnly
              placeholder="2025-05-20T14:30"
              className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-400 dark:text-gray-500"
            />
          </div>
        </div>

        {/* Template Fields Preview */}
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-gray-800">
            <div className="flex items-center">
              <FileText className="h-4 w-4 text-navy-600 dark:text-navy-400 mr-2" />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Fields to Extract (12)</span>
            </div>
            <ChevronRight className="h-4 w-4 text-gray-500" />
          </div>
        </div>

        {/* Attachments */}
        <div>
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">Attachments <span className="text-red-500">*</span></label>
          <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between bg-gray-50 dark:bg-gray-800 rounded p-2">
                <div className="flex items-center">
                  <Mail className="h-4 w-4 text-gray-400 mr-2" />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Purchase_Order_PO813.pdf</span>
                  <span className="text-xs text-gray-400 dark:text-gray-500 ml-2">(245 KB)</span>
                </div>
                <button className="text-red-500 cursor-default">
                  <X className="h-4 w-4" />
                </button>
              </div>
              <div className="flex items-center justify-center py-2 text-sm text-navy-600 dark:text-navy-400 cursor-default">
                <Plus className="h-4 w-4 mr-1" />
                Add more files
              </div>
            </div>
          </div>
          <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-1">Only PDF files are accepted. Drag &amp; drop supported.</p>
        </div>
      </div>

      {/* Modal Footer */}
      <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
        <button className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 cursor-default">
          Cancel
        </button>
        <button className="px-4 py-2 text-sm font-medium rounded-lg bg-navy-800 text-white flex items-center gap-2 cursor-default">
          <Mail className="h-4 w-4" />
          Ingest Email
        </button>
      </div>
    </div>
  )
}

// ─── Section 7: Duplicate Detection Mockup ───────────────────────────────────
function DuplicateDetectionMockup() {
  return (
    <div className="space-y-4 max-w-2xl">
      {/* Warning (reprocessing allowed) */}
      <div className="border border-amber-200 dark:border-amber-800 rounded-lg overflow-hidden">
        <div className="flex items-center gap-2 px-4 py-3 bg-amber-50 dark:bg-amber-900/20">
          <svg className="h-4 w-4 text-amber-600 dark:text-amber-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
            <line x1="12" y1="9" x2="12" y2="13" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
          <span className="text-sm font-medium text-amber-800 dark:text-amber-200">Already Processed with this Template</span>
        </div>
        <div className="p-4">
          <div className="flex items-center justify-between gap-3 p-3 rounded-lg bg-gray-50 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700">
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Purchase_Order_PO813.pdf</p>
              <div className="flex items-center gap-3 mt-1 text-xs text-gray-500 dark:text-gray-400">
                <span><strong>Last run:</strong> May 19, 2025</span>
                <span><strong>Request ID:</strong> #42</span>
                <span><strong>Status:</strong> <span className="text-green-600 dark:text-green-400">✓ completed</span></span>
                <span>12 fields extracted</span>
              </div>
            </div>
            <button className="px-2.5 py-1 text-xs font-medium rounded-md border border-gray-200 dark:border-gray-600 text-navy-600 dark:text-navy-400 cursor-default">
              View Results →
            </button>
          </div>
        </div>
        <div className="px-4 pb-3 flex items-center justify-between">
          <p className="text-xs text-amber-700 dark:text-amber-300">⚠️ Duplicate detected - reprocessing is allowed for this template.</p>
          <button className="px-3 py-1.5 text-xs font-medium rounded-md border border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-300 cursor-default">
            Reprocess
          </button>
        </div>
      </div>

      {/* Blocked (reprocessing disabled) */}
      <div className="border border-red-200 dark:border-red-800 rounded-lg overflow-hidden">
        <div className="flex items-center gap-2 px-4 py-3 bg-red-50 dark:bg-red-900/20">
          <svg className="h-4 w-4 text-red-600 dark:text-red-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <span className="text-sm font-medium text-red-800 dark:text-red-200">Already Processed with this Template</span>
        </div>
        <div className="px-4 py-3">
          <p className="text-xs font-medium text-red-700 dark:text-red-300">🚫 Reprocessing is disabled for this template. Submission blocked.</p>
        </div>
      </div>
    </div>
  )
}

// ─── Section 8: Email Detail Page Mockup ─────────────────────────────────────
function EmailDetailMockup() {
  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
      {/* Header */}
      <div className="p-5 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center">
          <button className="mr-4 cursor-default">
            <ArrowLeft className="h-6 w-6 text-gray-400 dark:text-gray-500" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100 truncate">PO-2024-0813 Purchase Order</h1>
            <p className="text-gray-500 dark:text-gray-400 text-sm">Email #47</p>
          </div>
          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-300">
            processed
          </span>
        </div>
      </div>

      {/* Two-column layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-0">
        {/* Main Content (2/3) */}
        <div className="lg:col-span-2 p-5 border-r border-gray-200 dark:border-gray-700 space-y-5">
          {/* Email Content */}
          <div className="space-y-3">
            <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Email Content</h2>
            <div className="space-y-2.5">
              <div className="flex items-center gap-2">
                <User className="h-4 w-4 text-gray-400" />
                <div>
                  <p className="text-[10px] text-gray-500 dark:text-gray-400">From</p>
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-200">procurement@techsupply.co</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-indigo-400" />
                <div>
                  <p className="text-[10px] text-gray-500 dark:text-gray-400">Shared Mailbox</p>
                  <p className="text-sm font-medium text-indigo-600 dark:text-indigo-400">orders@yourcompany.com</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-gray-400" />
                <div>
                  <p className="text-[10px] text-gray-500 dark:text-gray-400">Received</p>
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-200">May 19, 2025 at 14:32</p>
                </div>
              </div>
            </div>
            {/* Body */}
            <div className="border-t border-gray-200 dark:border-gray-700 pt-3">
              <h3 className="text-xs font-medium text-gray-900 dark:text-gray-100 mb-1.5">Body</h3>
              <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 text-xs text-gray-700 dark:text-gray-300 max-h-20 overflow-hidden">
                Please find the attached Purchase Order PO-2024-0813 for our quarterly supply order. Kindly confirm receipt and provide delivery estimates...
              </div>
            </div>
          </div>

          {/* Attachments */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Paperclip className="h-4 w-4 text-gray-400" />
              <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Attachments (2)</h2>
            </div>
            <div className="divide-y divide-gray-100 dark:divide-gray-700 rounded-lg border border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between px-3 py-2.5">
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-red-500" />
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">Purchase_Order_PO813.pdf</p>
                    <p className="text-[10px] text-gray-500 dark:text-gray-400">245 KB • application/pdf</p>
                  </div>
                </div>
                <button className="px-2.5 py-1 text-xs font-medium rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 cursor-default">View</button>
              </div>
              <div className="flex items-center justify-between px-3 py-2.5">
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-red-500" />
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">Specifications_v2.pdf</p>
                    <p className="text-[10px] text-gray-500 dark:text-gray-400">128 KB • application/pdf</p>
                  </div>
                </div>
                <button className="px-2.5 py-1 text-xs font-medium rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 cursor-default">View</button>
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar (1/3) */}
        <div className="p-5 space-y-4">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Details</h2>
          <div className="space-y-2.5">
            <div className="flex justify-between text-sm">
              <span className="text-gray-500 dark:text-gray-400">Status</span>
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-300">processed</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-500 dark:text-gray-400">Ingested</span>
              <span className="text-gray-900 dark:text-gray-100 font-medium">May 19, 2025</span>
            </div>
          </div>
        </div>
      </div>

      {/* Related Requests Table */}
      <div className="border-t border-gray-200 dark:border-gray-700 p-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Requests (1)</h2>
          <button className="p-1.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 cursor-default">
            <RefreshCw className="h-3.5 w-3.5" />
          </button>
        </div>
        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                <th className="px-4 py-2.5 text-left text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Instrument</th>
                <th className="px-4 py-2.5 text-left text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Issuer</th>
                <th className="px-4 py-2.5 text-left text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Processed Date</th>
                <th className="px-4 py-2.5 text-left text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                <th className="px-4 py-2.5 text-left text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">AI Confidence</th>
                <th className="px-4 py-2.5 text-left text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Created By</th>
                <th className="px-4 py-2.5"></th>
              </tr>
            </thead>
            <tbody>
              <tr className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center relative text-green-600 bg-green-50 dark:text-green-400 dark:bg-green-900/20">
                      <FileText className="h-5 w-5" />
                      <span className="absolute -top-1 -right-1 bg-navy-600 text-white text-[10px] font-medium rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">2</span>
                    </div>
                    <div>
                      <span className="font-medium text-gray-900 dark:text-gray-100 text-sm">PO-2024-0813</span>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Purchase order extraction</p>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">TechSupply Co.</td>
                <td className="px-4 py-3">
                  <p className="text-sm text-gray-900 dark:text-gray-100">May 19, 2025</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">14:32</p>
                </td>
                <td className="px-4 py-3">
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-300">
                    <span className="w-1.5 h-1.5 rounded-full bg-green-500"></span>
                    Completed
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <div className="w-16 h-1.5 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                      <div className="h-full rounded-full bg-green-500" style={{ width: '95%' }} />
                    </div>
                    <span className="text-sm text-gray-600 dark:text-gray-400">95%</span>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-xs font-medium text-gray-600 dark:text-gray-300">JD</div>
                    <span className="text-sm text-gray-600 dark:text-gray-400">Jane Doe</span>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <button className="p-1.5 rounded-lg text-gray-400 cursor-default">
                    <Edit3 className="h-4 w-4" />
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════════════════
// Main Guide Component
// ═══════════════════════════════════════════════════════════════════════════════
export default function EmailQueueGuide({ onNavigate }) {
  return (
    <div className="space-y-10">
      {/* Module Header */}
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-xl flex items-center justify-center">
          <Mail className="h-6 w-6 text-blue-600 dark:text-blue-400" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">Email Queue</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">Monitor and manage incoming email submissions</p>
        </div>
      </div>

      {/* Section 1 */}
      <Section number="1" title="Page Header & Actions">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          The Email Queue page header shows the title and two action buttons: <strong>Refresh</strong> to reload the list and <strong>Ingest Email</strong> to manually submit an email for processing.
        </p>
        <PageHeaderMockup />
      </Section>

      {/* Section 2 */}
      <Section number="2" title="Stats Cards">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Four summary cards appear at the top showing real-time counts: <strong>Total Emails</strong> (navy background), <strong>Queued</strong> (pending), <strong>Processing</strong> (currently being analyzed), and <strong>Processed</strong> (successfully completed). Stats update when filters change.
        </p>
        <StatsCardsMockup />
      </Section>

      {/* Section 3 */}
      <Section number="3" title="Status Tabs">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Quick-filter tabs let you switch between status views. The active tab is highlighted in navy. Each tab shows its icon for visual identification.
        </p>
        <StatusTabsMockup />
      </Section>

      {/* Section 4 */}
      <Section number="4" title="Search & Time Range Filter">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Below the status tabs, a search bar lets you search by subject or sender. The time range dropdown filters by when the email was received.
        </p>
        <SearchFilterMockup />
      </Section>

      {/* Section 5 */}
      <Section number="5" title="Emails Table">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          The table displays emails with 6 columns: <strong>Subject</strong> (with link to request if processed), <strong>Sender</strong> (with shared mailbox indicator if applicable), <strong>Documents</strong> (attachment count), <strong>Status</strong> (color-coded badge), <strong>Received</strong> (date), and <strong>Actions</strong> (View button). Clicking a row navigates to the Email Detail page.
        </p>
        <EmailTableMockup />
      </Section>

      {/* Section 6 */}
      <Section number="6" title="Ingest Email Modal">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Click "Ingest Email" to open a modal for manual email submission. Required fields: <strong>Subject</strong>, <strong>Sender</strong>, <strong>Template</strong>, and at least one <strong>PDF attachment</strong>. Optional fields: Recipients, Body, Received At. The template selector determines which AI analyzer processes the attachments. A collapsible "Fields to Extract" preview shows what the chosen template will look for.
        </p>
        <IngestEmailMockup />
      </Section>

      {/* Section 7 */}
      <Section number="7" title="Duplicate Detection">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          When you upload a file that has already been processed with the selected template, the system alerts you. If the template allows reprocessing, a <strong>warning</strong> (amber) appears with a "Reprocess" button. If reprocessing is disabled, the submission is <strong>blocked</strong> (red) and you cannot proceed.
        </p>
        <DuplicateDetectionMockup />
      </Section>

      {/* Section 8 */}
      <Section number="8" title="Email Detail Page">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Click any email row or "View" button to see the Email Detail page. It shows a <strong>2/3 + 1/3 layout</strong>: the left panel has email content (From, Shared Mailbox, Received, Body) and Attachments list with View links. The right sidebar shows metadata (Status, Ingested date). Below, a full-width <strong>Requests</strong> table lists all requests created from this email.
        </p>
        <EmailDetailMockup />
      </Section>

      {/* Tips */}
      <section className="space-y-4">
        <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Tips</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="rounded-xl bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 p-4">
            <div className="flex items-center gap-2 mb-1.5">
              <Search className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              <span className="text-xs font-semibold text-blue-900 dark:text-blue-200">Search & Filter Together</span>
            </div>
            <p className="text-[11px] text-blue-700 dark:text-blue-300 leading-relaxed">Combine status tabs with text search and time range to narrow down exactly the emails you need.</p>
          </div>
          <div className="rounded-xl bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 p-4">
            <div className="flex items-center gap-2 mb-1.5">
              <Upload className="h-4 w-4 text-purple-600 dark:text-purple-400" />
              <span className="text-xs font-semibold text-purple-900 dark:text-purple-200">PDF Only Attachments</span>
            </div>
            <p className="text-[11px] text-purple-700 dark:text-purple-300 leading-relaxed">The Ingest Email form only accepts PDF files. Non-PDF files are rejected with an error message.</p>
          </div>
          <div className="rounded-xl bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 p-4">
            <div className="flex items-center gap-2 mb-1.5">
              <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
              <span className="text-xs font-semibold text-green-900 dark:text-green-200">Track Processing Progress</span>
            </div>
            <p className="text-[11px] text-green-700 dark:text-green-300 leading-relaxed">Use the stats cards and "Processing" tab to monitor how many emails are currently being analyzed.</p>
          </div>
          <div className="rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 p-4">
            <div className="flex items-center gap-2 mb-1.5">
              <Paperclip className="h-4 w-4 text-amber-600 dark:text-amber-400" />
              <span className="text-xs font-semibold text-amber-900 dark:text-amber-200">Shared Mailbox Support</span>
            </div>
            <p className="text-[11px] text-amber-700 dark:text-amber-300 leading-relaxed">Emails from shared mailboxes are indicated with a purple mailbox icon in both the table and detail view.</p>
          </div>
        </div>
      </section>

      {/* Related Modules */}
      <section>
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">Related Modules</h3>
        <div className="flex flex-wrap gap-2">
          {[
            { id: 'requests', label: 'Requests', icon: FileText },
            { id: 'documents', label: 'Documents', icon: Files },
            { id: 'templates', label: 'Templates', icon: Layers },
          ].map((mod) => {
            const Icon = mod.icon
            return (
              <button key={mod.label} onClick={() => onNavigate(mod.id)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-xs font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors cursor-pointer">
                <Icon className="h-3.5 w-3.5" />
                {mod.label}
                <ArrowRight className="h-3 w-3 text-gray-400" />
              </button>
            )
          })}
        </div>
      </section>
    </div>
  )
}
